import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as DocIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle as SuccessIcon,
  Lock as LockIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/authStore';
import { documentService } from '../services/documentService';
import RichTextEditor from '../components/RichTextEditor';
import { createRichTextContent, textToEditorDocument } from '../utils/richTextDocument';
import { stitchColors, stitchFonts } from '../theme/tokens';

const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.doc'];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

export const DocumentBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [activeTab, setActiveTab] = useState<'upload' | 'manual'>('upload');
  const [documentTitle, setDocumentTitle] = useState('');
  const [manualText, setManualText] = useState('');
  const [editorContent, setEditorContent] = useState(() => textToEditorDocument(''));
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submittingManual, setSubmittingManual] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isEstateOwner = user?.userType === 'EO';
  const isAllowedUser = isEstateOwner;

  const validateFile = (file: File): string | null => {
    const lowerName = file.name.toLowerCase();
    const hasValidExt = ALLOWED_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
    if (!hasValidExt) {
      return 'Invalid file type. Only PDF (.pdf) and Word documents (.docx, .doc) are supported.';
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return 'File size exceeds the 10MB limit. Please upload a smaller file.';
    }
    return null;
  };

  const handleFileSelection = (file: File) => {
    setError(null);
    setSuccess(null);
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSelectedFile(file);
    if (!documentTitle.trim()) {
      const derived = file.name.replace(/\.(pdf|docx|doc)$/i, '').replace(/[-_]/g, ' ');
      setDocumentTitle(derived);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (!isAllowedUser) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (isAllowedUser) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCreateManual = async () => {
    if (!manualText.trim()) {
      setError('Please enter the text for your document.');
      return;
    }

    try {
      setSubmittingManual(true);
      setError(null);
      setSuccess(null);

      const result = await documentService.createManualDocument({
        title: documentTitle.trim() || undefined,
        text: manualText.trim(),
        editorContent,
      });

      setSuccess('Document successfully created. Redirecting...');

      setTimeout(() => {
        const docId = (result as any)?._id || (result as any)?.id;
        if (docId) {
          navigate(`/documents/${docId}`);
        } else {
          navigate('/document-library');
        }
      }, 1200);
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create manual document. Please try again.';
      setError(message);
    } finally {
      setSubmittingManual(false);
    }
  };

  const handleUploadAndCreate = async () => {
    if (!selectedFile) {
      setError('Please select a PDF or Word document to upload.');
      return;
    }

    if (!documentTitle.trim()) {
      setError('Please enter a document title.');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const result = await documentService.uploadDocumentImport({
        file: selectedFile,
        title: documentTitle.trim(),
      });

      if (result.status === 'failed') {
        setError(result.warnings?.[0] || 'Document extraction failed. Please check the file and try again.');
        return;
      }

      setSuccess('Document successfully created and contents extracted. Redirecting...');

      setTimeout(() => {
        if (result.documentId) {
          navigate(`/documents/${result.documentId}`);
        } else {
          navigate('/document-library');
        }
      }, 1200);
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to upload and extract document. Please check the file and try again.';
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box
      sx={{
        maxWidth: 840,
        mx: 'auto',
        p: { xs: 2, md: 4 },
        fontFamily: stitchFonts.body,
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontFamily: stitchFonts.headline,
            fontWeight: 700,
            color: stitchColors.onBackground,
            letterSpacing: '-0.02em',
            mb: 1,
          }}
        >
          Document Builder
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: stitchColors.onSurfaceVariant,
            lineHeight: 1.6,
          }}
        >
          Upload your existing document in PDF or Word format (.pdf, .docx, .doc). Our OCR system
          will extract the text verbatim and create a new legal document in your Document History.
        </Typography>
      </Box>

      {!isAllowedUser && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            border: `1px solid ${stitchColors.outlineVariant}`,
            bgcolor: stitchColors.surfaceContainerLowest,
            textAlign: 'center',
          }}
        >
          <LockIcon sx={{ fontSize: 48, color: stitchColors.outline, mb: 1.5 }} />
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            Authorized Users Only
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto', mb: 3 }}>
            Document creation is exclusively available to Estate Owners (EO).
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/document-library')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Go to Document History
          </Button>
        </Paper>
      )}

      {isAllowedUser && (
        <Stack spacing={3}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              icon={<SuccessIcon fontSize="inherit" />}
              sx={{ borderRadius: 2 }}
            >
              {success}
            </Alert>
          )}

          <Tabs
            value={activeTab}
            onChange={(_, val) => {
              setActiveTab(val);
              setError(null);
            }}
            sx={{ borderBottom: 1, borderColor: stitchColors.outlineVariant }}
          >
            <Tab label="Upload Document" value="upload" icon={<UploadIcon />} iconPosition="start" />
            <Tab label="Write Manually" value="manual" icon={<EditIcon />} iconPosition="start" />
          </Tabs>

          {activeTab === 'manual' && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3.5 },
                borderRadius: 2,
                border: `1px solid ${stitchColors.outlineVariant}`,
                bgcolor: stitchColors.surfaceContainerLowest,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: stitchFonts.headline,
                  fontWeight: 600,
                  color: stitchColors.onBackground,
                  mb: 2,
                }}
              >
                Write Will Document
              </Typography>

              <TextField
                label="Document Title"
                id="document-title-manual-input"
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value)}
                placeholder="e.g. Last Will and Testament of John Doe"
                required
                fullWidth
                disabled={submittingManual}
                helperText="Give your document a clear, identifiable name."
                sx={{ mb: 3 }}
              />

              <RichTextEditor
                content={editorContent}
                preserveLayout
                editable={!submittingManual}
                onChange={(content) => {
                  setEditorContent(content);
                  setManualText(createRichTextContent(content).bodyText);
                }}
              />

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  disabled={submittingManual}
                  onClick={() => navigate('/document-library')}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={submittingManual || !manualText.trim()}
                  onClick={handleCreateManual}
                  startIcon={
                    submittingManual ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <DocIcon />
                    )
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                  }}
                >
                  {submittingManual ? 'Creating Document...' : 'Save & Create Document'}
                </Button>
              </Box>
            </Paper>
          )}

          {activeTab === 'upload' && (

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              borderRadius: 2,
              border: `1px solid ${stitchColors.outlineVariant}`,
              bgcolor: stitchColors.surfaceContainerLowest,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: stitchFonts.headline,
                fontWeight: 600,
                color: stitchColors.onBackground,
                mb: 2,
              }}
            >
              Document Details
            </Typography>

            <TextField
              label="Document Title"
              id="document-title-input"
              value={documentTitle}
              onChange={(e) => setDocumentTitle(e.target.value)}
              placeholder="e.g. Last Will and Testament of John Doe"
              required
              fullWidth
              disabled={uploading}
              helperText="Give your document a clear, identifiable name."
              sx={{ mb: 3 }}
            />

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: stitchColors.onBackground,
                mb: 1,
              }}
            >
              Upload Your Own Document
            </Typography>

            <input
              ref={fileInputRef}
              type="file"
              data-testid="document-file-input"
              accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleFileSelection(e.target.files[0]);
                }
              }}
            />

            {!selectedFile ? (
              <Box
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: `2px dashed ${isDragging ? stitchColors.primary : stitchColors.outlineVariant}`,
                  borderRadius: 2,
                  p: { xs: 3, md: 5 },
                  textAlign: 'center',
                  bgcolor: isDragging ? stitchColors.surfaceContainerLow : stitchColors.surface,
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, background-color 0.2s',
                  '&:hover': {
                    borderColor: stitchColors.primary,
                    bgcolor: stitchColors.surfaceContainerLow,
                  },
                }}
              >
                <UploadIcon sx={{ fontSize: 48, color: stitchColors.primary, mb: 1.5 }} />
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                  Click to select or drag and drop a document
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Supports PDF (.pdf) and Word documents (.docx, .doc) up to 10MB
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  Browse Files
                </Button>
              </Box>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  bgcolor: stitchColors.surfaceContainerLow,
                  borderColor: stitchColors.outlineVariant,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  {selectedFile.name.toLowerCase().endsWith('.pdf') ? (
                    <PdfIcon color="error" sx={{ fontSize: 36, flexShrink: 0 }} />
                  ) : (
                    <DocIcon color="primary" sx={{ fontSize: 36, flexShrink: 0 }} />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                      {selectedFile.name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(selectedFile.size)}
                      </Typography>
                      <Chip
                        label={selectedFile.name.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Word Doc'}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                      />
                    </Stack>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleClearFile}
                  disabled={uploading}
                  aria-label="Remove selected file"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Paper>
            )}

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
              <Button
                variant="outlined"
                color="inherit"
                disabled={uploading}
                onClick={() => navigate('/document-library')}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={uploading || !selectedFile}
                onClick={handleUploadAndCreate}
                startIcon={
                  uploading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <UploadIcon />
                  )
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                }}
              >
                {uploading ? 'Extracting & Creating...' : 'Upload & Create Document'}
              </Button>
            </Box>
          </Paper>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default DocumentBuilder;
